/**
 * Formats a date string for display with configurable locale and fallback.
 *
 * Examples:
 *   formatDate(dateString)              → "2 minutes ago" or "14 Jun 2026, 14:30"
 *   formatDate(dateString, 'short')     → "14 Jun 2026" (date only, no relative)
 *   formatDate(dateString, 'relative')  → forces relative even for older dates
 *
 * Options object form:
 *   formatDate(dateString, { mode: 'auto', locale: 'en-US', fallback: 'N/A' })
 */
export function formatDate(dateStringOrDate, options = {}) {
  if (typeof options === "string") {
    options = { mode: options };
  }

  const {
    mode = "auto",
    locale = "en-GB",
    fallback = "",
    includeTime = true,
  } = options;

  if (!dateStringOrDate) return fallback;

  const date = new Date(dateStringOrDate);
  if (isNaN(date.getTime())) return fallback;

  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (mode === "relative") {
    return relativeTime(diffMins, diffHours, diffDays);
  }

  if (mode === "short") {
    return date.toLocaleDateString(locale, {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  }

  if (mode === "auto") {
    if (diffMins < 60 || diffHours < 24 || diffDays < 7) {
      return relativeTime(diffMins, diffHours, diffDays);
    }

    const formatOptions = {
      day: "2-digit",
      month: "short",
      year: "numeric",
    };

    if (includeTime) {
      formatOptions.hour = "2-digit";
      formatOptions.minute = "2-digit";
    }

    return date.toLocaleDateString(locale, formatOptions);
  }

  return date.toLocaleDateString(locale, {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function relativeTime(diffMins, diffHours, diffDays) {
  if (diffMins < 1) return "just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return null;
}
